import { ChangeDetectorRef, NgZone, OnDestroy, Pipe, PipeTransform, inject } from '@angular/core';

interface TimeUnit {
  readonly single: string;
  readonly plural: string;
  readonly milliseconds: number;
  readonly threshold?: number;
}

const TIME_UNITS: readonly TimeUnit[] = [
  { single: 'year', plural: 'years', milliseconds: 365 * 24 * 60 * 60 * 1000 },
  { single: 'month', plural: 'months', milliseconds: 30.416 * 24 * 60 * 60 * 1000 },
  { single: 'day', plural: 'days', milliseconds: 24 * 60 * 60 * 1000 },
  { single: 'hour', plural: 'hours', milliseconds: 60 * 60 * 1000 },
  { single: 'minute', plural: 'minutes', milliseconds: 60 * 1000 },
  { single: 'second', plural: 'seconds', milliseconds: 1000 },
] as const;

@Pipe({
  name: 'timeAgo',
  pure: false,
  standalone: true,
})
export class TimeAgoPipe implements PipeTransform, OnDestroy {
  readonly #changeDetectorRef: ChangeDetectorRef;
  readonly #ngZone: NgZone;
  #timer: number | null = null;

  constructor(changeDetectorRef?: ChangeDetectorRef, ngZone?: NgZone) {
    this.#changeDetectorRef = changeDetectorRef || inject(ChangeDetectorRef);
    this.#ngZone = ngZone || inject(NgZone);
  }

  transform(value: string | Date | number | null | undefined, options?: { suffix?: boolean }): string {
    this.#clearTimer();

    if (!value) {
      return '';
    }

    const date = this.#parseDate(value);
    if (!date || isNaN(date.getTime())) {
      return '';
    }

    const now = new Date();
    const diffMs = Math.abs(now.getTime() - date.getTime());
    const suffix = options?.suffix !== false ? ' ago' : '';

    // Handle edge cases
    if (diffMs < 1000) {
      return `just now`;
    }

    const result = this.#formatTimeAgo(diffMs);
    const updateInterval = this.#getUpdateInterval(diffMs);

    this.#scheduleUpdate(updateInterval);

    return result + suffix;
  }

  ngOnDestroy(): void {
    this.#clearTimer();
  }

  #parseDate(value: string | Date | number): Date | null {
    if (value instanceof Date) {
      return value;
    }

    if (typeof value === 'number') {
      return new Date(value);
    }

    if (typeof value === 'string') {
      // Handle ISO strings and timestamps
      const parsed = Date.parse(value);
      return isNaN(parsed) ? null : new Date(parsed);
    }

    return null;
  }

  #formatTimeAgo(diffMs: number): string {
    for (const unit of TIME_UNITS) {
      const count = Math.floor(diffMs / unit.milliseconds);
      if (count >= 1) {
        // Special handling for "1 unit" vs "n units"
        if (count === 1) {
          // Special cases for single units
          if (unit.single === 'hour') return 'an hour';
          if (unit.single === 'day') return 'a day';
          if (unit.single === 'month') return 'a month';
          if (unit.single === 'year') return 'a year';
          return `a ${unit.single}`;
        }

        // Special handling for "about" thresholds
        if (unit.single === 'month' && count >= 11) {
          return 'about a year';
        }
        if (unit.single === 'day' && count >= 26) {
          return 'about a month';
        }
        if (unit.single === 'hour' && count >= 22) {
          return 'a day';
        }
        if (unit.single === 'minute' && count >= 45) {
          return 'about an hour';
        }

        return `${count} ${unit.plural}`;
      }
    }

    return 'just now';
  }

  #getUpdateInterval(diffMs: number): number {
    // Update more frequently for recent times, less frequently for older times
    const minutes = diffMs / (60 * 1000);
    const hours = diffMs / (60 * 60 * 1000);
    const days = diffMs / (24 * 60 * 60 * 1000);

    if (minutes < 1) {
      return 2 * 1000; // Update every 2 seconds
    } else if (hours < 1) {
      return 30 * 1000; // Update every 30 seconds
    } else if (days < 1) {
      return 5 * 60 * 1000; // Update every 5 minutes
    } else {
      return 60 * 60 * 1000; // Update every hour
    }
  }

  #scheduleUpdate(interval: number): void {
    if (typeof window === 'undefined') {
      return;
    }

    this.#timer = this.#ngZone.runOutsideAngular(() => {
      return window.setTimeout(() => {
        this.#ngZone.run(() => this.#changeDetectorRef.markForCheck());
      }, interval);
    });
  }

  #clearTimer(): void {
    if (this.#timer !== null && typeof window !== 'undefined') {
      window.clearTimeout(this.#timer);
      this.#timer = null;
    }
  }
}
